'use client'
import { useEffect, useState } from 'react'
import { Button } from '@/components/ui/button'
import { useRouter } from 'next/navigation'
import { useToast } from '@/hooks/use-toast'
import type { BillingInterval } from '@/lib/billing/plan-config'
import { devBypassActive } from '@/lib/billing/dev-bypass'

interface UpgradeButtonProps {
  /** Which plan to upgrade to (PRO) */
  plan: string
  /** Display name for the button label */
  planName: string
  /** If true, start a 7-day free trial via Stripe trial_period_days */
  trial?: boolean
  /** Packaging v2: which Pro billing term to check out (defaults monthly). */
  interval?: BillingInterval
  /** Optional referral code entered by the user on the pricing page */
  referralCode?: string
  /** Same-origin path to land on after checkout (sanitized server-side) —
   *  e.g. the practice session the user left to upgrade the tutor. */
  returnTo?: string | null
}

export default function UpgradeButton({
  plan,
  planName,
  trial,
  interval,
  referralCode,
  returnTo,
}: UpgradeButtonProps) {
  const router = useRouter()
  const { toast } = useToast()

  // Hydration-safe: SSR always renders the real checkout button (fail closed);
  // dev/preview/staging hosts swap in the bypass after mount.
  const [devBypass, setDevBypass] = useState(false)
  useEffect(() => {
    setDevBypass(devBypassActive())
  }, [])

  if (devBypass) {
    return (
      <Button
        className="mt-2 w-full bg-brand hover:bg-brand-hover"
        onClick={() => router.push('/sat')}
        title="Pro bypass is ON in dev and preview"
      >
        Pro bypass is ON
      </Button>
    )
  }

  const label = trial ? 'Start 7-day free trial' : `Upgrade to ${planName}`

  // production: real checkout
  return (
    <Button
      className="w-full"
      onClick={async () => {
        try {
          const r = await fetch('/api/stripe/checkout', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              plan,
              trial: trial === true,
              billingInterval: interval ?? 'monthly',
              referralCode: referralCode || undefined,
              returnTo: returnTo || undefined,
            }),
          })
          // 409 = you already have an active subscription (Stripe source of
          // truth). Don't generic-alert — guide the user to manage it.
          if (r.status === 409) {
            const { error } = await r
              .json()
              .catch(() => ({ error: 'You already have an active subscription.' }))
            toast({
              title: 'You already have an active subscription',
              description: error ?? 'Manage it from your settings.',
            })
            router.push('/settings')
            return
          }
          if (!r.ok) throw new Error(await r.text())
          const { url } = await r.json()
          window.location.href = url
        } catch (e: any) {
          alert(`Checkout failed: ${e.message}`)
        }
      }}
    >
      {label}
    </Button>
  )
}
